import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api, displayDate, displayNumber, errorMessage, toetsRoles } from './toetsUtils';
import './Toetsen.css';

export default function StudentToetsResults({ studentId, studentName = '' }) {
  const { userInfo } = useAuth();
  const allowed = toetsRoles.includes(userInfo?.role);
  const accessKey = `${userInfo?.user_id || ''}:${userInfo?.customer_id || ''}:${userInfo?.role || ''}:${(userInfo?.user_location_ids || []).join(',')}`;
  const [results, setResults] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    if (!allowed || !studentId) return undefined;
    const controller = new AbortController();
    setLoading(true); setError(''); setResults([]);
    // Geen schooljaarfilter: ook beschikbare resultaten van eerdere jaren tonen.
    api('get', '', undefined, {
      params: { student_id: studentId, page, limit: 10 }, signal: controller.signal
    }).then(response => {
      setResults(response.data.data); setPagination(response.data.pagination);
    }).catch(err => { if (!axios.isCancel(err)) setError(errorMessage(err)); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [studentId, allowed, accessKey, page, refresh]);
  if (!allowed || !studentId) return null;
  const allLink = `/toetsen?student_id=${encodeURIComponent(studentId)}&student_name=${encodeURIComponent(studentName)}&school_year=all`;
  return <section className="toets-panel mb-4" aria-label="Toetsresultaten van deze leerling">
    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
      <div><h3 className="h5 mb-1">Toetsresultaten</h3><span className="text-muted small">Alle schooljaren · meest recente toetsdatum eerst</span></div>
      <Link className="btn btn-sm btn-outline-primary" to={allLink}>Alle resultaten bekijken</Link>
    </div>
    {loading && <p role="status" className="text-muted mb-0">Toetsresultaten laden…</p>}
    {error && <div role="alert" className="alert alert-danger mb-0">{error}
      <button type="button" className="btn btn-sm btn-outline-danger ms-2" onClick={() => setRefresh(v => v + 1)}>Opnieuw laden</button>
    </div>}
    {!loading && !error && <>
      {results.length ? <div className="table-responsive"><table className="table toetsen-table mb-0">
        <thead><tr><th scope="col">Titel</th><th scope="col">Schooljaar</th><th scope="col">Datum</th>
          <th scope="col">Goed</th><th scope="col">Fout</th><th scope="col">Cijfer</th><th scope="col">Acties</th></tr></thead>
        <tbody>{results.map(result => <tr key={result.id}>
          <td className="toets-title"><Link to={`/toetsen/${result.id}`}>{result.title}</Link>
            {result.part_count > 0 && <div className="text-muted small">{result.part_count} onderdelen</div>}</td>
          <td>{result.school_year || '—'}</td><td className="text-nowrap">{displayDate(result.test_date)}</td>
          <td>{displayNumber(result.correct_count)}</td><td>{displayNumber(result.incorrect_count)}</td><td>{displayNumber(result.grade)}</td>
          <td><Link className="btn btn-sm btn-outline-primary" to={`/toetsen/${result.id}`}>Bekijken</Link></td>
        </tr>)}</tbody>
      </table></div> : <p className="text-muted mb-0">Er zijn nog geen toetsresultaten voor deze leerling.</p>}
      {pagination.total > 0 && <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
        <small>{pagination.total} toetsresultaten · pagina {pagination.page} van {pagination.totalPages}</small>
        {pagination.totalPages > 1 && <nav className="d-flex gap-2" aria-label="Paginering toetsresultaten leerling">
          <button type="button" className="btn btn-sm btn-outline-primary" disabled={!pagination.hasPrevPage} onClick={() => setPage(pagination.page - 1)}>Vorige</button>
          <button type="button" className="btn btn-sm btn-outline-primary" disabled={!pagination.hasNextPage} onClick={() => setPage(pagination.page + 1)}>Volgende</button>
        </nav>}
      </div>}
    </>}
  </section>;
}
